import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import {
  Abrechnungsart, ABRECHNUNGSART_LABEL, AbrechnungHistorieEintrag, Artikel, Vertrag,
  Vertragsartikel, VertragsartikelUebersicht,
} from '../types';
import vertragsartikelService from '../services/vertragsartikelService';
import rechnungService from '../services/rechnungService';
import { RECHNUNG_STATUS_LABEL } from '../config/rechnungStatus';
import artikelService from '../services/artikelService';
import {
  Card, Button, Badge, Icon, Modal, Field, Input, Select, Textarea, FormActions,
  Spinner, EmptyState, inputClass,
} from './ui';
import { ankunft, markiereNeu } from '../utils/ankunft';
import { formatBetrag, formatPreis, formatMwst, formatMenge, zahlZuText, textZuZahl } from '../utils/preis';
import { fmtDatum, jahrestag, laufendesVertragsjahr, vertragsjahrText } from '../utils/vertragsjahr';

type Art = 'EINMALIG' | 'JAEHRLICH' | 'VERLAENGERUNG' | 'PACHT';

/**
 * Position anlegen/bearbeiten. Der Artikel wird über ein Suchfeld gewählt – ein <Select> über
 * mehrere hundert Artikel ist auf dem iPad unbenutzbar. Preis und MwSt kommen als Vorschlag aus
 * der Preisliste und bleiben an der Position frei änderbar (Snapshot) – außer bei der
 * Pachtverlängerung, die immer mit dem aktuellen Preis des Artikels rechnet.
 *
 * Beim Hinzufügen fragt ein zweiter Schritt, **wann** zum ersten Mal berechnet wird: jetzt als
 * eigene Rechnung für das laufende Vertragsjahr, oder mit der nächsten Jahresrechnung am Jahrestag.
 */
const VertragsartikelFormModal: React.FC<{
  vertrag: Vertrag;
  position?: Vertragsartikel | null;
  artikel: Artikel[];
  onClose: () => void;
  onSaved: () => void;
}> = ({ vertrag, position, artikel, onClose, onSaved }) => {
  const navigate = useNavigate();
  const vertragId = vertrag.id;
  const editing = position ?? null;
  const laufend = laufendesVertragsjahr(vertrag.unterschriftsdatum);
  const naechsteAbrechnung = fmtDatum(jahrestag(vertrag.unterschriftsdatum, laufend + 1));

  const [artikelId, setArtikelId] = useState<number | null>(editing?.artikelId ?? null);
  const [suche, setSuche] = useState('');
  const [art, setArt] = useState<Art>((editing?.abrechnungsart as Art) ?? 'JAEHRLICH');
  const [abJahrText, setAbJahrText] = useState(String(editing?.gueltigAbJahr ?? laufend));
  const [bisJahrText, setBisJahrText] = useState(
    editing?.gueltigBisJahr != null ? String(editing.gueltigBisJahr) : '',
  );
  const [mengeText, setMengeText] = useState(zahlZuText(editing?.menge ?? 1));
  const [preisText, setPreisText] = useState(zahlZuText(editing?.einzelpreis ?? 0));
  const [mwstText, setMwstText] = useState(zahlZuText(editing?.mwstSatz ?? 19));
  const [notizen, setNotizen] = useState(editing?.notizen ?? '');
  const [saving, setSaving] = useState(false);
  // Zweiter Schritt beim Hinzufügen: die geprüften Daten warten auf die Antwort „jetzt oder später?“.
  const [wannFrage, setWannFrage] = useState<Record<string, unknown> | null>(null);

  const gewaehlt = artikel.find(a => a.id === artikelId) ?? null;

  // Trefferliste erst ab einem Suchbegriff – sonst würden hunderte Zeilen gerendert.
  const treffer = useMemo(() => {
    const q = suche.trim().toLowerCase();
    if (!q) return [];
    return artikel
      .filter(a => a.active && `${a.artikelnummer} ${a.bezeichnung}`.toLowerCase().includes(q))
      .slice(0, 25);
  }, [artikel, suche]);

  /** Artikelauswahl belegt Preis und MwSt vor (Snapshot aus der aktuellen Preisliste). */
  const waehleArtikel = (a: Artikel) => {
    setArtikelId(a.id);
    setSuche('');
    setPreisText(zahlZuText(a.preis ?? 0));
    setMwstText(zahlZuText(a.mwstSatz));
  };

  // Live-Vorschau mit derselben Rechenregel wie im Backend (Geldrechner).
  const vorschau = useMemo(() => {
    const menge = textZuZahl(mengeText);
    const preis = textZuZahl(preisText);
    const satz = textZuZahl(mwstText);
    if (menge == null || preis == null || satz == null) return null;
    const netto = Math.round(menge * preis * 100) / 100;
    const mwst = Math.round(netto * satz) / 100;
    return { netto, mwst, brutto: Math.round((netto + mwst) * 100) / 100 };
  }, [mengeText, preisText, mwstText]);

  const speichere = async (daten: Record<string, unknown>, sofort: boolean) => {
    setSaving(true);
    try {
      const neu = await vertragsartikelService.create(vertragId, daten as any);
      markiereNeu(`vertragsartikel-${neu.id}`);
      if (sofort) {
        const rechnung = await rechnungService.sofortrechnung(vertragId, laufend);
        toast.success('Position hinzugefügt, Rechnungsentwurf angelegt – bitte prüfen und stellen');
        navigate(`/rechnungen/${rechnung.id}`);
        return;
      }
      toast.success(`Position hinzugefügt – wird am ${naechsteAbrechnung} berechnet`);
      onSaved();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Position konnte nicht hinzugefügt werden');
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (artikelId == null) { toast.error('Bitte einen Artikel wählen'); return; }

    const menge = textZuZahl(mengeText);
    if (menge == null || menge <= 0) { toast.error('Menge muss größer als 0 sein'); return; }
    const einzelpreis = textZuZahl(preisText);
    if (einzelpreis == null) { toast.error('Preis ist keine gültige Zahl'); return; }
    const mwstSatz = textZuZahl(mwstText);
    if (mwstSatz == null || mwstSatz > 100) { toast.error('MwSt-Satz muss zwischen 0 und 100 liegen'); return; }
    const gueltigAbJahr = parseInt(abJahrText, 10);
    if (!Number.isFinite(gueltigAbJahr)) { toast.error('Bitte ein gültiges Vertragsjahr angeben'); return; }
    const gueltigBisJahr = bisJahrText.trim() === '' ? null : parseInt(bisJahrText, 10);
    if (gueltigBisJahr != null && (!Number.isFinite(gueltigBisJahr) || gueltigBisJahr < gueltigAbJahr)) {
      toast.error('Das Endjahr darf nicht vor dem Startjahr liegen'); return;
    }

    const daten = {
      artikelId,
      abrechnungsart: art as Abrechnungsart,
      periodeId: null,
      gueltigAbJahr,
      gueltigBisJahr: art === 'EINMALIG' ? null : gueltigBisJahr,
      menge, einzelpreis, mwstSatz,
      notizen: notizen.trim() || null,
    };

    if (!editing) {
      // Die Pachtverlängerung beginnt immer mit dem Ende der Laufzeit – da gibt es nichts zu fragen.
      if (art === 'VERLAENGERUNG') { await speichere(daten, false); return; }
      setWannFrage(daten);
      return;
    }

    setSaving(true);
    try {
      await vertragsartikelService.update(editing.id, { ...daten, active: editing.active });
      markiereNeu(`vertragsartikel-${editing.id}`);
      toast.success('Position gespeichert');
      onSaved();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Position konnte nicht gespeichert werden');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editing || !window.confirm(`Position „${editing.artikelBezeichnung ?? editing.artikelnummer ?? ''}“ entfernen? Bereits gestellte Rechnungen bleiben unverändert.`)) return;
    setSaving(true);
    try {
      await vertragsartikelService.delete(editing.id);
      toast.success('Position entfernt');
      onSaved();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Position konnte nicht entfernt werden');
    } finally {
      setSaving(false);
    }
  };

  // Zweiter Schritt: jetzt als eigene Rechnung oder mit der nächsten Jahresrechnung?
  if (wannFrage) {
    const name = gewaehlt?.bezeichnung ?? 'die Leistung';
    return (
      <Modal title="Wann berechnen?" onClose={onClose}>
        <div className="space-y-3">
          <p className="text-sm text-gray-700">
            Wann soll <strong>{name}</strong> {art === 'JAEHRLICH' ? 'zum ersten Mal' : ''} berechnet werden?
          </p>
          <button
            type="button"
            disabled={saving}
            onClick={() => speichere({ ...wannFrage, gueltigAbJahr: laufend }, true)}
            className="w-full rounded-lg border border-gray-200 px-4 py-3 text-left transition-colors hover:border-brand-300 hover:bg-brand-50 disabled:opacity-50"
          >
            <p className="text-sm font-medium text-gray-900">Jetzt abrechnen</p>
            <p className="text-xs text-gray-500">
              Eigene Rechnung für das laufende Vertragsjahr {vertragsjahrText(vertrag.unterschriftsdatum, laufend)}
              {art === 'JAEHRLICH' ? `, danach jedes Jahr am Jahrestag (${naechsteAbrechnung.slice(0, 6)})` : ''}.
            </p>
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={() => speichere({ ...wannFrage, gueltigAbJahr: laufend + 1 }, false)}
            className="w-full rounded-lg border border-gray-200 px-4 py-3 text-left transition-colors hover:border-brand-300 hover:bg-brand-50 disabled:opacity-50"
          >
            <p className="text-sm font-medium text-gray-900">Mit der nächsten Jahresrechnung am {naechsteAbrechnung}</p>
            <p className="text-xs text-gray-500">
              Für das Vertragsjahr {vertragsjahrText(vertrag.unterschriftsdatum, laufend + 1)}
              {art === 'JAEHRLICH' ? ', danach jedes Jahr' : ''}.
            </p>
          </button>
          <FormActions>
            <Button type="button" variant="secondary" disabled={saving} onClick={() => setWannFrage(null)}>Zurück</Button>
          </FormActions>
        </div>
      </Modal>
    );
  }

  const hinweis =
    art === 'JAEHRLICH' ? 'Jedes Vertragsjahr einmal, im Voraus zum Jahrestag der Unterschrift.'
      : art === 'VERLAENGERUNG' ? 'Jährlich ab Ende der Laufzeit, bis der Vertrag beendet wird – immer zum aktuellen Preis aus der Preisliste.'
        : art === 'PACHT' ? 'Alte Grabpacht: Laufzeit am Stück, danach je Verlängerung. Für neue Verträge Grundpacht einmalig + Pachtverlängerung verwenden.'
          : 'Wird genau ein Mal berechnet.';

  return (
    <Modal title={editing ? 'Position bearbeiten' : 'Position hinzufügen'} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Field label="Artikel" required>
          {gewaehlt ? (
            <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-gray-900">{gewaehlt.bezeichnung}</p>
                <p className="text-xs text-gray-500">
                  {gewaehlt.artikelnummer} · {formatPreis(gewaehlt.preis)} · {formatMwst(gewaehlt.mwstSatz)}
                </p>
              </div>
              <Button type="button" size="sm" variant="secondary" onClick={() => setArtikelId(null)}>Ändern</Button>
            </div>
          ) : (
            <>
              <input
                autoFocus
                placeholder="Artikelnummer oder Bezeichnung suchen…"
                value={suche}
                onChange={e => setSuche(e.target.value)}
                className={inputClass}
              />
              {treffer.length > 0 && (
                <ul className="mt-2 max-h-48 divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200">
                  {treffer.map(a => (
                    <li key={a.id}>
                      <button
                        type="button"
                        onClick={() => waehleArtikel(a)}
                        className="flex w-full items-baseline gap-2 px-3 py-2 text-left hover:bg-gray-50"
                      >
                        <span className="min-w-0 flex-1 truncate text-sm text-gray-900">{a.bezeichnung}</span>
                        <span className="whitespace-nowrap text-xs tabular-nums text-gray-500">{formatPreis(a.preis)}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {suche.trim() !== '' && treffer.length === 0 && (
                <p className="mt-2 text-sm text-gray-500">Kein Artikel gefunden.</p>
              )}
            </>
          )}
        </Field>

        <Field label="Abrechnung" required hint={hinweis}>
          <Select required value={art} onChange={e => setArt(e.target.value as Art)}>
            <option value="EINMALIG">Einmalig</option>
            <option value="JAEHRLICH">Jährlich</option>
            <option value="VERLAENGERUNG">Pachtverlängerung — jährlich nach der Laufzeit</option>
            {editing?.abrechnungsart === 'PACHT' && <option value="PACHT">Grabpacht (alt)</option>}
          </Select>
        </Field>

        {/* Beim Bearbeiten lässt sich der Zeitraum korrigieren; beim Hinzufügen fragt der zweite Schritt. */}
        {editing && art !== 'VERLAENGERUNG' && art !== 'PACHT' && (
          <div className="grid grid-cols-2 gap-3">
            <Field
              label={art === 'JAEHRLICH' ? 'Ab Vertragsjahr' : 'Im Vertragsjahr'}
              required
              hint={Number.isFinite(parseInt(abJahrText, 10)) ? vertragsjahrText(vertrag.unterschriftsdatum, parseInt(abJahrText, 10)) : undefined}
            >
              <Input required inputMode="numeric" value={abJahrText} onChange={e => setAbJahrText(e.target.value)} />
            </Field>
            {art === 'JAEHRLICH' && (
              <Field label="Bis Vertragsjahr" hint="Leer = bis der Vertrag endet">
                <Input inputMode="numeric" placeholder="unbefristet" value={bisJahrText} onChange={e => setBisJahrText(e.target.value)} />
              </Field>
            )}
          </div>
        )}

        <div className="grid grid-cols-3 gap-3">
          <Field label="Menge" required>
            <Input required inputMode="decimal" value={mengeText} onChange={e => setMengeText(e.target.value)} />
          </Field>
          {art === 'VERLAENGERUNG' ? (
            // Die Verlängerung rechnet immer mit dem aktuellen Preis aus der Preisliste.
            <p className="col-span-2 self-end pb-2 text-sm text-gray-600">
              Preis laut Preisliste{gewaehlt ? `: ${formatPreis(gewaehlt.preis)} netto` : ''} — Preisänderungen gelten automatisch.
            </p>
          ) : (
            <>
              <Field label="Preis netto" required>
                <Input required inputMode="decimal" value={preisText} onChange={e => setPreisText(e.target.value)} />
              </Field>
              <Field label="MwSt %" required>
                <Input required inputMode="decimal" value={mwstText} onChange={e => setMwstText(e.target.value)} />
              </Field>
            </>
          )}
        </div>

        {vorschau && art !== 'VERLAENGERUNG' && (
          <p className="rounded-lg bg-brand-50 px-3 py-2 text-sm text-brand-900">
            Netto {formatBetrag(vorschau.netto)} · MwSt {formatBetrag(vorschau.mwst)} ·{' '}
            <strong>Brutto {formatBetrag(vorschau.brutto)}</strong>
          </p>
        )}

        <Field label="Notizen">
          <Textarea rows={2} value={notizen} onChange={e => setNotizen(e.target.value)} />
        </Field>

        <FormActions>
          {editing && (
            <Button type="button" variant="danger" className="mr-auto" disabled={saving} onClick={handleDelete}>
              Entfernen
            </Button>
          )}
          <Button type="button" variant="secondary" onClick={onClose}>Abbrechen</Button>
          <Button type="submit" loading={saving}>{editing ? 'Speichern' : 'Weiter'}</Button>
        </FormActions>
      </form>
    </Modal>
  );
};

/**
 * Die Artikel/Leistungen eines Vertrags samt Summen. Preis und MwSt sind Snapshots aus der
 * Preisliste – ein späterer Import verändert bestehende Positionen nicht.
 *
 * Darstellung bewusst als gestapelte Zeilen statt breiter Tabelle: das Portal wird primär auf
 * dem iPad hochkant (~504 px) bedient, dort passt keine 5-spaltige Tabelle ohne Seitwärts-Scroll.
 */
const VertragsartikelSektion: React.FC<{ vertrag: Vertrag }> = ({ vertrag }) => {
  const vertragId = vertrag.id;
  const [uebersicht, setUebersicht] = useState<VertragsartikelUebersicht | null>(null);
  const [historie, setHistorie] = useState<AbrechnungHistorieEintrag[]>([]);
  const [artikel, setArtikel] = useState<Artikel[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Vertragsartikel | null>(null);

  const load = useCallback(async () => {
    try {
      const [u, h] = await Promise.all([
        vertragsartikelService.getByVertrag(vertragId),
        // Die Historie darf fehlschlagen, ohne die Positionen mitzureißen.
        rechnungService.getHistorie(vertragId).catch(() => [] as AbrechnungHistorieEintrag[]),
      ]);
      setUebersicht(u);
      setHistorie(h);
    } catch {
      toast.error('Positionen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [vertragId]);

  useEffect(() => { load(); }, [load]);

  /** Stammdaten erst beim Öffnen des Formulars holen – die Sektion selbst braucht sie nicht. */
  const openForm = async (position?: Vertragsartikel) => {
    setEditing(position ?? null);
    setShowForm(true);
    if (artikel.length > 0) return;
    try {
      setArtikel(await artikelService.getAll());
    } catch {
      toast.error('Artikel konnten nicht geladen werden');
    }
  };

  const closeForm = () => { setShowForm(false); setEditing(null); };
  const handleSaved = () => { closeForm(); load(); };

  const positionen = uebersicht?.positionen ?? [];

  return (
    <Card className="p-6">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-base font-semibold text-gray-900">Artikel &amp; Leistungen</h2>
        <Button size="sm" variant="secondary" onClick={() => openForm()} icon={<Icon.Plus className="h-4 w-4" />}>
          Position hinzufügen
        </Button>
      </div>

      {showForm && (
        artikel.length === 0 ? (
          <Modal title="Position hinzufügen" onClose={closeForm}>
            <div className="flex items-center gap-2 py-6 text-sm text-gray-500">
              <Spinner /> Artikel werden geladen…
            </div>
          </Modal>
        ) : (
          <VertragsartikelFormModal
            vertrag={vertrag}
            position={editing}
            artikel={artikel}
            onClose={closeForm}
            onSaved={handleSaved}
          />
        )
      )}

      {/* Beim Umstellen des Altbestands leicht zu übersehen: ohne Pachtposition wird die
          Grabpacht nie berechnet. */}
      {!loading && uebersicht && !uebersicht.hatPachtposition && (
        <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
          Für diesen Vertrag ist keine Grabpacht hinterlegt — sie wird deshalb nicht abgerechnet.
        </p>
      )}

      {loading ? (
        <div className="flex items-center gap-2 py-4 text-sm text-gray-500"><Spinner /> Lädt…</div>
      ) : positionen.length === 0 ? (
        <EmptyState
          icon={<Icon.Doc />}
          title="Noch keine Positionen"
          hint="Hier stehen die Leistungen, die für diesen Vertrag abgerechnet werden."
          action={
            <Button size="sm" onClick={() => openForm()} icon={<Icon.Plus className="h-4 w-4" />}>
              Position hinzufügen
            </Button>
          }
        />
      ) : (
        <>
          <ul className="divide-y divide-gray-100">
            {positionen.map(p => { const a = ankunft(`vertragsartikel-${p.id}`); return (
              <li key={p.id} ref={a.ref} className={a.className}>
                <button
                  type="button"
                  onClick={() => openForm(p)}
                  className="w-full py-3 text-left transition-colors hover:bg-gray-50/70"
                  title="Position bearbeiten"
                >
                  <div className="flex items-baseline gap-2">
                    <span className="min-w-0 flex-1 break-words text-sm font-medium text-gray-900">
                      {p.artikelBezeichnung ?? 'Unbekannter Artikel'}
                    </span>
                    <Badge className="shrink-0 bg-brand-50 text-brand-800">
                      {ABRECHNUNGSART_LABEL[p.abrechnungsart]}
                      {p.periodeName ? ` · ${p.periodeName}${p.periodeTerminText ? ` ${p.periodeTerminText}` : ''}` : ''}
                    </Badge>
                  </div>
                  <div className="mt-1 flex items-baseline gap-2 text-xs text-gray-500">
                    <span className="min-w-0 flex-1 truncate tabular-nums">
                      {formatMenge(p.menge)} × {formatPreis(p.einzelpreis)}
                      {p.abrechnungsart === 'PACHT' || p.abrechnungsart === 'VERLAENGERUNG' ? '/Jahr' : ''} · {formatMwst(p.mwstSatz)}
                      {' · '}
                      {p.abrechnungsart === 'EINMALIG'
                        ? `Vertragsjahr ${vertragsjahrText(vertrag.unterschriftsdatum, p.gueltigAbJahr)}`
                        : `ab ${fmtDatum(jahrestag(vertrag.unterschriftsdatum, p.gueltigAbJahr))}${p.gueltigBisJahr ? ` bis Vertragsjahr ${p.gueltigBisJahr}` : ''}`}
                    </span>
                    <span className="whitespace-nowrap tabular-nums font-medium text-gray-900">
                      {formatBetrag(p.bruttoBetrag)}
                    </span>
                  </div>
                  {p.artikelGeloescht && (
                    <Badge className="mt-1 bg-amber-100 text-amber-800">Artikel gelöscht</Badge>
                  )}
                  {p.notizen && <p className="mt-1 break-words text-xs text-gray-500">{p.notizen}</p>}
                </button>

                {/* Abrechnungs-Historie: welches Jahr wurde mit welcher Rechnung berechnet. */}
                {historie.filter(h => h.vertragsartikelId === p.id).length > 0 && (
                  <div className="mb-2 ml-0 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
                    {historie
                      .filter(h => h.vertragsartikelId === p.id)
                      .sort((a, b) => (a.abrechnungsjahr ?? 0) - (b.abrechnungsjahr ?? 0))
                      .map(h => (
                        <span key={`${h.rechnungId}-${h.abrechnungsjahr}`}>
                          <strong className="text-gray-700">{h.abrechnungsjahr}</strong>{': '}
                          <Link to={`/rechnungen/${h.rechnungId}`} className="text-brand-700 hover:underline">
                            {h.rechnungsnummer ?? 'Entwurf'}
                          </Link>
                          {' '}({RECHNUNG_STATUS_LABEL[h.status]})
                        </span>
                      ))}
                  </div>
                )}
              </li>
            ); })}
          </ul>

          {uebersicht && (
            <dl className="mt-4 space-y-1 border-t border-gray-200 pt-3 text-sm">
              <div className="flex justify-between gap-4">
                <dt className="text-gray-500">Netto</dt>
                <dd className="tabular-nums text-gray-900">{formatBetrag(uebersicht.summeNetto)}</dd>
              </div>
              {uebersicht.mwstJeSatz.map(a => (
                <div key={a.satz} className="flex justify-between gap-4">
                  <dt className="text-gray-500">zzgl. {formatMwst(a.satz)} MwSt</dt>
                  <dd className="tabular-nums text-gray-900">{formatBetrag(a.mwst)}</dd>
                </div>
              ))}
              <div className="flex justify-between gap-4 border-t border-gray-100 pt-1">
                <dt className="font-semibold text-gray-900">Brutto</dt>
                <dd className="tabular-nums font-semibold text-gray-900">{formatBetrag(uebersicht.summeBrutto)}</dd>
              </div>
            </dl>
          )}
        </>
      )}
    </Card>
  );
};

export default VertragsartikelSektion;
